import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Modal from "../../../../parts/modal-new";
import SvgIcon from "../../../../parts/SvgIcon";
import getCycleCardImagePath from "../../../../utils/get-cycle-card-image-path";
import ImgComponent from "../../../../parts/ImgComponent";

const DESCRIPTION_MAX = 100;

/**
 * Shared list used by create step 2 and the standalone manage modal.
 */
export const WorkoutFolderWorkoutsPicker = ({
	savedWorkouts = [],
	workoutFolders = [],
	selectedIds,
	onToggle,
	folderId,
	search,
	onSearchChange,
}) => {
	const { t } = useTranslation("coaching-portal");

	const folderByWorkoutId = useMemo(() => {
		const map = {};
		for (const folder of workoutFolders) {
			if (folderId && String(folder._id) === String(folderId)) continue;
			for (const id of folder.workoutIds || []) {
				map[String(id)] = folder.name;
			}
		}
		return map;
	}, [workoutFolders, folderId]);

	const filtered = useMemo(() => {
		const q = (search || "").trim().toLowerCase();
		if (!q) return savedWorkouts;
		return savedWorkouts.filter(
			(w) =>
				(w.name || "").toLowerCase().includes(q) ||
				(w.description || "").toLowerCase().includes(q),
		);
	}, [savedWorkouts, search]);

	const selectedSet = useMemo(
		() => new Set((selectedIds || []).map(String)),
		[selectedIds],
	);

	return (
		<div className="workout-folder-workouts-picker">
			<input
				type="search"
				placeholder={t(
					"allTemplates.workoutFolders.searchWorkouts",
					"Search Workout..",
				)}
				value={search}
				onChange={(e) => onSearchChange(e.target.value)}
			/>
			<h4 className="workout-folder-workouts-list-title">
				{t("allTemplates.workoutFolders.workouts", "Workouts")}
			</h4>
			<ul className="workout-folder-workouts-list">
				{filtered.map((workout) => {
					const id = String(workout._id);
					const isSelected = selectedSet.has(id);
					const otherFolder = folderByWorkoutId[id];
					return (
						<li key={id} className="workout-folder-workouts-item">
							<div className="workout-folder-workouts-item-thumb">
								<ImgComponent
									image={getCycleCardImagePath(workout)}
									altText={workout.name || ""}
									classNames="program-image"
								/>
							</div>
							<div className="workout-folder-workouts-item-text">
								<span className="workout-folder-workouts-item-name">
									{workout.name}
								</span>
								<span className="workout-folder-workouts-item-description">
									{workout.description ||
										t(
											"allTemplates.workoutFolders.noWorkoutDescription",
											"Description goes here",
										)}
								</span>
								{otherFolder && !isSelected && (
									<span className="workout-folder-workouts-item-other">
										{t("allTemplates.workoutFolders.inFolder", {
											name: otherFolder,
											defaultValue: "In {{name}}",
										})}
									</span>
								)}
							</div>
							<button
								type="button"
								className={
									isSelected
										? "workout-folder-workouts-remove"
										: "workout-folder-workouts-add"
								}
								data-button-type={isSelected ? "secondary" : "outline"}
								onClick={() => onToggle(id)}
							>
								{isSelected
									? t("allTemplates.workoutFolders.remove", "Remove")
									: t("allTemplates.workoutFolders.add", "+ Add")}
							</button>
						</li>
					);
				})}
				{filtered.length === 0 && (
					<li className="workout-folder-workouts-empty">
						{t(
							"allTemplates.workoutFolders.noWorkoutsFound",
							"No workouts found",
						)}
					</li>
				)}
			</ul>
		</div>
	);
};

WorkoutFolderWorkoutsPicker.propTypes = {
	savedWorkouts: PropTypes.array,
	workoutFolders: PropTypes.array,
	selectedIds: PropTypes.array,
	onToggle: PropTypes.func.isRequired,
	folderId: PropTypes.string,
	search: PropTypes.string,
	onSearchChange: PropTypes.func.isRequired,
};

const HandleWorkoutFolderModal = ({ locals }) => {
	const { t } = useTranslation("coaching-portal");
	const { query = {}, workoutFolders = [], savedWorkouts = [] } = locals;
	const folderId = query.targetFolderId;
	const existing = folderId
		? workoutFolders.find((f) => String(f._id) === String(folderId))
		: null;
	const isEdit = Boolean(existing);

	const [step, setStep] = useState(1);
	const [name, setName] = useState(existing?.name || "");
	const [description, setDescription] = useState(existing?.description || "");
	const [selectedIds, setSelectedIds] = useState(
		(existing?.workoutIds || []).map(String),
	);
	const [search, setSearch] = useState("");
	const [submitting, setSubmitting] = useState(false);

	const descriptionLen = description.length;

	const toggleWorkout = (id) => {
		setSelectedIds((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
		);
	};

	const closeModal = () => {
		submit("/actions/refresh", {
			method: "GET",
			body: { modal: "", targetFolderId: "" },
		});
	};

	const saveFolder = ({ withWorkouts } = {}) => {
		if (!name.trim() || submitting) return;
		setSubmitting(true);
		const body = {
			...(isEdit ? { id: folderId } : {}),
			name: name.trim(),
			description: description.trim(),
			...(withWorkouts ? { workoutIds: selectedIds } : {}),
		};
		submit("/actions/workout-folders", {
			method: "POST",
			body,
		});
	};

	const handleNext = () => {
		if (!name.trim()) return;
		setStep(2);
	};

	const handleSaveEdit = () => {
		saveFolder({ withWorkouts: false });
	};

	const handleCreate = () => {
		saveFolder({ withWorkouts: true });
	};

	// Step 2 of create flow — manage workouts
	if (!isEdit && step === 2) {
		return (
			<Modal
				className="workout-folder-modal manage-workout-folder-modal"
				title={t(
					"allTemplates.workoutFolders.addWorkoutsTitle",
					"Add workouts",
				)}
				toClear={["targetFolderId"]}
			>
				<div className="body">
					<div className="workout-folder-manage-summary">
						<SvgIcon src="/img/new-icons/folder.svg" />
						<div>
							<h4>{name}</h4>
							<p>
								{t("allTemplates.workoutFolders.workoutsInFolder", {
									count: selectedIds.length,
									defaultValue_one: "{{count}} workout in this folder",
									defaultValue_other: "{{count}} workouts in this folder",
								})}
							</p>
						</div>
					</div>
					<WorkoutFolderWorkoutsPicker
						savedWorkouts={savedWorkouts}
						workoutFolders={workoutFolders}
						selectedIds={selectedIds}
						onToggle={toggleWorkout}
						search={search}
						onSearchChange={setSearch}
					/>
				</div>
				<footer>
					<button
						type="button"
						data-button-type="secondary"
						data-action="cancel"
						onClick={closeModal}
					>
						{t("allTemplates.workoutFolders.cancel", "Cancel")}
					</button>
					<button
						type="button"
						data-button-type="primary"
						disabled={submitting}
						onClick={handleCreate}
					>
						{t(
							"allTemplates.workoutFolders.createFolderAction",
							"Create Folder",
						)}
					</button>
				</footer>
			</Modal>
		);
	}

	return (
		<Modal
			className="workout-folder-modal handle-workout-folder-modal"
			title={
				isEdit
					? t("allTemplates.workoutFolders.editFolderTitle", "Edit folder")
					: t(
							"allTemplates.workoutFolders.createFolderTitle",
							"Create new folder",
						)
			}
			toClear={["targetFolderId"]}
		>
			<div className="body">
				<label className="workout-folder-field">
					<span>
						{t("allTemplates.workoutFolders.folderName", "Folder Name")}
					</span>
					<input
						type="text"
						maxLength={60}
						placeholder={t(
							"allTemplates.workoutFolders.folderNamePlaceholder",
							"Enter Name",
						)}
						value={name}
						onChange={(e) => setName(e.target.value)}
					/>
				</label>
				<label className="workout-folder-field">
					<span className="workout-folder-field-row">
						<span>
							{t(
								"allTemplates.workoutFolders.descriptionOptional",
								"Description (optional)",
							)}
						</span>
						<span className="workout-folder-char-count">
							{t("allTemplates.workoutFolders.charCount", {
								count: descriptionLen,
								max: DESCRIPTION_MAX,
								defaultValue: "{{count}}/{{max}} characters",
							})}
						</span>
					</span>
					<textarea
						maxLength={DESCRIPTION_MAX}
						rows={3}
						placeholder={t(
							"allTemplates.workoutFolders.descriptionPlaceholder",
							"Enter description here",
						)}
						value={description}
						onChange={(e) => setDescription(e.target.value)}
					/>
				</label>
			</div>
			<footer className="workout-folder-modal-footer">
				{!isEdit && (
					<div className="workout-folder-next-hint">
						<span>
							{t("allTemplates.workoutFolders.nextStep", "Next step:")}
						</span>
						<span className="workout-folder-next-badge">
							{t("allTemplates.workoutFolders.addWorkouts", "Add workouts")}
						</span>
					</div>
				)}
				<div className="workout-folder-modal-actions">
					<button
						type="button"
						data-button-type="secondary"
						data-action="cancel"
						onClick={closeModal}
					>
						{t("allTemplates.workoutFolders.cancel", "Cancel")}
					</button>
					{isEdit ? (
						<button
							type="button"
							data-button-type="primary"
							disabled={!name.trim() || submitting}
							onClick={handleSaveEdit}
						>
							{t("allTemplates.workoutFolders.save", "Save")}
						</button>
					) : (
						<button
							type="button"
							data-button-type="primary"
							disabled={!name.trim()}
							onClick={handleNext}
						>
							{t("allTemplates.workoutFolders.next", "Next")}
						</button>
					)}
				</div>
			</footer>
		</Modal>
	);
};

HandleWorkoutFolderModal.propTypes = {
	locals: PropTypes.object.isRequired,
};

export default HandleWorkoutFolderModal;
